import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import {
  Mic, Send, Sparkles, Loader2, ArrowLeft, Camera, Monitor,
  X, VideoOff, Star, Activity, Copy, Check, ChevronDown, Zap,
  MessageSquare, ListOrdered,
} from "lucide-react";
import SpeechReflectionPanel from "../components/SpeechReflectionPanel";

// How long the interviewer must stay quiet before we commit to asking the
// AI. Every new transcript segment cancels and restarts this wait, so mid-
// question breathing pauses just keep accumulating into the same question
// instead of firing early. Too short and a natural pause gets treated as
// "done talking" — an answer is generated for the partial question, then
// the rest of the question arrives moments later and gets asked (and
// answered) again as if new, making the on-screen answer look like it
// keeps regenerating while the interviewer is still mid-question.
const AUTO_SEND_SILENCE_MS = 3000;

// ── Q-type badge colours ──────────────────────────────────────────────────────
const Q_TYPE_COLOR = {
  "Behavioral":      "text-blue-400   border-blue-500/30   bg-blue-500/10",
  "Technical":       "text-purple-400 border-purple-500/30 bg-purple-500/10",
  "Situational":     "text-orange-400 border-orange-500/30 bg-orange-500/10",
  "Competency":      "text-teal-400   border-teal-500/30   bg-teal-500/10",
  "Domain-Specific": "text-[#D3FF24]  border-[#D3FF24]/30  bg-[#D3FF24]/10",
  "Company/Culture": "text-pink-400   border-pink-500/30   bg-pink-500/10",
};

// Strip markdown bold/italic so answers render as clean plain text
const stripMd = (t = "") =>
  t.replace(/\*\*(.+?)\*\*/gs, "$1").replace(/\*([^*\n]+?)\*/g, "$1").trim();

// Append newly-transcribed text onto the running question, trimming any
// duplicated words at the seam. System-audio capture uses overlapping audio
// segments (so no words are dropped between segments), which means
// consecutive Whisper transcriptions can repeat the same trailing phrase.
function mergeTranscript(prev, next) {
  if (!prev) return next;
  if (!next) return prev;
  const prevWords = prev.trim().split(/\s+/);
  const nextWords = next.trim().split(/\s+/);
  const maxOverlap = Math.min(prevWords.length, nextWords.length, 12);
  for (let n = maxOverlap; n > 0; n--) {
    const tail = prevWords.slice(-n).join(" ").toLowerCase().replace(/[.,!?]/g, "");
    const head = nextWords.slice(0, n).join(" ").toLowerCase().replace(/[.,!?]/g, "");
    if (tail === head) return `${prev} ${nextWords.slice(n).join(" ")}`.trim();
  }
  return `${prev} ${next}`;
}

// ── Parse AI answer ────────────────────────────────────────────────────────────
function parseAnswers(text) {
  const qTypeMatch  = text.match(/\*\*Q TYPE:\*\*\s*([^*\n]+)/i);
  const bestMatch   = text.match(/\*\*(?:★\s*)?BEST ANSWER:\*\*\s*([\s\S]*?)(?=\*\*(?:[⚡▸★]?\s*)?(?:FOLLOW-UP PREP|OPTION [BC23]):|$)/i);
  const followMatch = text.match(/\*\*(?:⚡\s*)?FOLLOW-UP PREP:\*\*\s*(.+?)(?=\n\*\*|$)/is);
  const optBMatch   = text.match(/\*\*(?:▸\s*)?OPTION [B2]:\*\*\s*([\s\S]*?)(?=\*\*(?:▸\s*)?OPTION [C3]:|$)/i);
  const optCMatch   = text.match(/\*\*(?:▸\s*)?OPTION [C3]:\*\*\s*([\s\S]*?)$/i);
  return {
    qType:   qTypeMatch  ? qTypeMatch[1].trim()  : null,
    best:    bestMatch   ? bestMatch[1].trim()   : text.trim(),
    followUp:followMatch ? followMatch[1].trim() : null,
    alts: [
      ...(optBMatch ? [{ label: "Option B", text: optBMatch[1].trim() }] : []),
      ...(optCMatch ? [{ label: "Option C", text: optCMatch[1].trim() }] : []),
    ],
  };
}

// Rebuild the left-panel AI-answer cards from a stored transcript when a past
// session is reopened from history. The transcript persists every turn as
// alternating {role:"user"} question / {role:"assistant"} answer entries (see
// backend copilot_ask); this pairs each answer with the question that preceded
// it and parses it into the same {question, best, qType, followUp, alts, ts}
// shape runAsk produces live. Returned newest-first, matching the live
// prepend order so `answers[0]` is the most recent Q&A.
function rebuildAnswers(transcript, isLive) {
  const out = [];
  for (let i = 0; i < transcript.length; i++) {
    const turn = transcript[i];
    if (turn.role !== "assistant") continue;
    const prev = transcript[i - 1];
    const question = prev && prev.role === "user" ? prev.content : "";
    const full = turn.content || "";
    const parsed = isLive ? parseAnswers(full) : { best: full, qType: null, followUp: null, alts: [] };
    out.push({ id: `saved-${i}`, question, ...parsed, status: "complete", ts: turn.ts ? new Date(turn.ts) : new Date() });
  }
  return out.reverse();
}

// ── Audio capture: one realtime channel ─────────────────────────────────────
//
// Both the candidate's mic and the interviewer's system/loopback audio run
// through the same pipeline: getUserMedia/getDisplayMedia → PCM16 via an
// AudioWorklet → streamed live to OpenAI's Realtime transcription API over a
// WebSocket (word-by-word partial captions, ~1s time-to-transcript), with a
// batch MediaRecorder+Whisper fallback if the realtime pipeline can't start
// or drops mid-session. The two channels run concurrently and independently
// — one failing (denied permission, unsupported platform) never tears down
// the other. Only the system/interviewer channel's completed transcripts are
// forwarded to onTranscript (and from there, auto-asked to the AI) — mic
// transcripts are tracked locally only, so an interviewer follow-up mid-
// answer is never lost to the candidate talking over it into their own mic.
//
// States: idle → waiting → speaking → transcribing → waiting → …
//
function useRealtimeChannel(channel, sessionId, onSegment, onTranscript, forwardsToAI, onExpired) {
  const [captureState, setCaptureState] = useState("idle");
  const [level,   setLevel]   = useState(0);
  const [active,  setActive]  = useState(false);
  const [partial, setPartial] = useState(""); // live interim caption (realtime mode only)

  const r = useRef({
    recorder:    null,   // MediaRecorder (batch-fallback mode)
    stream:      null,
    externalStream: false, // true when start() was given a stream we don't own (e.g. shared with the video panel) — don't stop its tracks on teardown
    intervalId:  null,
    active:      false,
    mimeType:    "audio/webm",
    ws:          null,   // realtime transcription socket
    audioCtx:    null,   // AudioContext feeding the PCM worklet (realtime mode)
    workletNode: null,
    segmentTimeouts: [],
  });

  // Send a blob to Whisper (batch fallback only)
  const whisperSend = useCallback(async (chunks, mimeType) => {
    if (!chunks.length) return;
    const blob = new Blob(chunks, { type: mimeType });
    if (blob.size < 3000) return;
    try {
      const fd = new FormData();
      fd.append("file", blob, "audio.webm");
      const res = await fetch("/api/transcribe", {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();
      const text = data.text?.trim();
      if (text) {
        onSegment?.(text);
        if (forwardsToAI) onTranscript(text);
      }
    } catch (_) {}
  }, [onSegment, onTranscript, forwardsToAI]);

  const teardown = useCallback(() => {
    const s = r.current;
    s.active = false;
    clearInterval(s.intervalId);
    try { if (s.recorder?.state !== "inactive") s.recorder.stop(); } catch (_) {}
    (s.segmentTimeouts || []).forEach(clearTimeout);
    s.segmentTimeouts = [];
    try { s.ws?.close(); } catch (_) {}
    s.ws = null;
    try { s.workletNode?.disconnect(); } catch (_) {}
    s.workletNode = null;
    try { s.audioCtx?.close(); } catch (_) {}
    s.audioCtx = null;
    // Externally-provided streams (e.g. the video panel's screen-share
    // stream, shared with this channel for its audio track) are owned by
    // whoever created them — don't stop tracks the video panel still needs.
    if (!s.externalStream) s.stream?.getTracks().forEach((t) => t.stop());
    s.stream = null; s.externalStream = false; s.recorder = null; s.intervalId = null;
    setPartial("");
  }, []);

  // ── Batch fallback: overlapping MediaRecorder segments → Whisper. Used
  // only if the realtime pipeline below can't start (old browser, blocked
  // WebSocket, etc.) or drops mid-session.
  const startBatchCapture = useCallback((stream) => {
    const s = r.current;
    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus" : "audio/webm";
    s.mimeType = mimeType;
    // Record audio only — a stream shared with the video panel also carries
    // a video track, and a MediaRecorder configured with an audio-only
    // mimeType throws if handed a stream that includes video.
    const audioStream = new MediaStream(stream.getAudioTracks());

    // Segments overlap so the stream is *always* being recorded — no gap
    // while the previous clip uploads to Whisper — and no word is lost by
    // landing exactly on a hard cut. The 2s overlap between consecutive
    // segments is de-duplicated on merge in onTranscript.
    const SEGMENT_LEN = 6000;
    const SEGMENT_INTERVAL = 4000;
    s.segmentTimeouts = [];

    const runSegment = () => {
      if (!s.active) return;
      const chunks = [];
      const rec = new MediaRecorder(audioStream, { mimeType });
      s.recorder = rec;
      rec.ondataavailable = (e) => { if (e.data?.size > 0) chunks.push(e.data); };
      rec.onstop = () => {
        if (!s.active) return;
        setCaptureState("transcribing");
        whisperSend(chunks, mimeType).finally(() => {
          if (s.active) setCaptureState("waiting");
        });
      };
      rec.start();
      s.segmentTimeouts.push(
        setTimeout(() => { try { if (rec.state !== "inactive") rec.stop(); } catch (_) {} }, SEGMENT_LEN)
      );
    };

    setCaptureState("waiting");
    runSegment();
    s.intervalId = setInterval(runSegment, SEGMENT_INTERVAL);
  }, [whisperSend]);

  // ── Realtime: stream PCM16 to OpenAI's Realtime transcription API via our
  // backend proxy. Server-side voice-activity detection marks each utterance
  // as its own "turn" (no fixed windows), and words stream back within a few
  // hundred ms instead of after a multi-second batch upload. Returns true if
  // it started OK.
  const startRealtimeCapture = useCallback(async (stream) => {
    const s = r.current;
    try {
      // OpenAI's Realtime transcription API caps the input sample rate at
      // 24000Hz. Letting the AudioContext default to the device's native
      // rate (commonly 44100 or 48000Hz) makes every session.update call
      // fail server-side with "integer above maximum value" — the realtime
      // path then always falls back to the slower batch/Whisper path.
      // Requesting 24000Hz explicitly makes the browser resample the input
      // track to match, so ctx.sampleRate (sent below) is always in range.
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC({ sampleRate: 24000 });
      await ctx.audioWorklet.addModule("/pcm-worklet.js");
      const source = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, "pcm-worklet");
      // Route through a muted gain so the audio graph stays "live" (worklets
      // only process while connected toward the destination) without ever
      // playing the call audio back out of the candidate's speakers.
      const mute = ctx.createGain();
      mute.gain.value = 0;
      source.connect(node);
      node.connect(mute);
      mute.connect(ctx.destination);

      const token = localStorage.getItem("token");
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(
        `${proto}//${window.location.host}/api/ws/transcribe/${encodeURIComponent(sessionId)}` +
        `?token=${encodeURIComponent(token)}&rate=${Math.round(ctx.sampleRate)}`
      );

      const opened = await new Promise((resolve) => {
        const timer = setTimeout(() => resolve(false), 6000);
        ws.onopen = () => { clearTimeout(timer); resolve(true); };
        ws.onerror = () => { clearTimeout(timer); resolve(false); };
      });
      if (!opened) {
        try { node.disconnect(); source.disconnect(); ctx.close(); } catch (_) {}
        try { ws.close(); } catch (_) {}
        return false;
      }

      s.audioCtx = ctx;
      s.workletNode = node;
      s.ws = ws;

      node.port.onmessage = (e) => {
        if (!s.active || ws.readyState !== WebSocket.OPEN) return;
        const bytes = new Uint8Array(e.data);
        let bin = "";
        for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
        ws.send(JSON.stringify({ type: "input_audio_buffer.append", audio: btoa(bin) }));
      };

      ws.onmessage = (ev) => {
        if (!s.active) return;
        let d;
        try { d = JSON.parse(ev.data); } catch (_) { return; }

        if (d.type === "speech_started") {
          setCaptureState("speaking"); setLevel(70);
        } else if (d.type === "speech_stopped") {
          setCaptureState("transcribing"); setLevel(0);
        } else if (d.type === "delta") {
          setPartial((p) => p + d.delta);
        } else if (d.type === "completed") {
          setPartial("");
          setCaptureState("waiting");
          const text = (d.transcript || "").trim();
          if (text) {
            onSegment?.(text);
            if (forwardsToAI) onTranscript(text);
          }
        }
        // "error" events are logged server-side; a setup-time error already
        // fails startRealtimeCapture below via the open/ack handshake.
      };

      ws.onclose = (ev) => {
        if (!s.active || s.ws !== ws) return; // stale handle from a previous attempt
        s.ws = null;
        // 4403/4408 are the backend's intentional 90-minute-cap closes (at
        // connect time and mid-session respectively) — falling back to
        // batch capture here would be wrong, since the batch path's
        // /transcribe endpoint has no session_id and so no cap awareness at
        // all. Anything else is an unexpected drop — keep the interview
        // going via the backup path.
        if (ev.code === 4403 || ev.code === 4408) {
          teardown();
          setActive(false); setCaptureState("idle"); setLevel(0);
          onExpired?.();
          return;
        }
        toast.error(
          `${channel === "mic" ? "Mic" : "Live"} transcription connection dropped — switching to backup capture.`
        );
        startBatchCapture(stream);
      };

      setCaptureState("waiting");
      return true;
    } catch (_) {
      return false;
    }
  }, [forwardsToAI, onSegment, onTranscript, startBatchCapture, sessionId, channel, teardown, onExpired]);

  // For the "system" channel, an already-acquired stream can be passed in
  // (the same getDisplayMedia grant the video panel uses to show the
  // interviewer's shared window) instead of this hook requesting its own —
  // one screen/window share then covers both video display and interviewer
  // audio transcription. In that case the caller owns the stream/video
  // tracks; this hook only reads the audio track and never stops it itself.
  const start = useCallback(async (providedStream) => {
    teardown();
    const s = r.current;
    s.active = true;

    try {
      let stream;
      if (providedStream) {
        stream = providedStream;
        s.externalStream = true;
        if (!stream.getAudioTracks().length) { s.active = false; return; }
      } else if (channel === "mic") {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
      } else {
        stream = await navigator.mediaDevices.getDisplayMedia({
          audio: true,
          video: { width: 1, height: 1, frameRate: 1 },
        });
        stream.getVideoTracks().forEach((t) => t.stop());
        if (!stream.getAudioTracks().length) {
          toast.error(
            'No system audio captured, so the interviewer\'s voice won\'t auto-trigger answers ' +
            '— your mic is still listening. If the call is a Chrome tab, re-share and pick that ' +
            'exact Tab with "Share tab audio" checked (most reliable). If it\'s a separate app or ' +
            'window (Teams, or Meet in another browser), share "Entire screen" instead and check ' +
            '"Share system audio". You can still type/paste interviewer questions manually.'
          );
          stream.getTracks().forEach((t) => t.stop());
          s.active = false;
          return;
        }
      }
      s.stream = stream;

      setActive(true);
      const realtimeOk = await startRealtimeCapture(stream);
      if (!realtimeOk) startBatchCapture(stream);

    } catch (e) {
      s.active = false;
      if (e.name !== "NotAllowedError") {
        toast.error(`${channel === "mic" ? "Microphone" : "System audio"}: ${e.message}`);
      }
    }
  }, [teardown, channel, startRealtimeCapture, startBatchCapture]);

  const stop = useCallback(() => {
    teardown();
    setActive(false); setCaptureState("idle"); setLevel(0);
  }, [teardown]);

  useEffect(() => () => teardown(), [teardown]);

  return { active, captureState, level, partial, start, stop };
}

// Runs the candidate's mic and the interviewer's system audio as two
// independent, concurrent channels. Every completed segment from either
// channel is reported to onSegment(speaker, text) for the raw dual-speaker
// transcript feed; only the system channel's transcripts are additionally
// forwarded to onTranscript/auto-ask — see useRealtimeChannel above.
//
// getDisplayMedia (system audio) requires a real user gesture — browsers
// reject it outright ("NotAllowedError: ... Must be handled by a user
// gesture") if called from an effect/timer instead of a click handler. Mic
// (getUserMedia) has no such restriction. So callers must auto-start mic
// only and start the system channel from an actual click.
function useAudioCapture(onTranscript, onSegment, sessionId, onExpired) {
  const mic    = useRealtimeChannel("mic",    sessionId, (t) => onSegment("candidate",   t), onTranscript, false, onExpired);
  const system = useRealtimeChannel("system", sessionId, (t) => onSegment("interviewer", t), onTranscript, true,  onExpired);

  const startAll = useCallback(() => { mic.start(); system.start(); }, [mic, system]);
  const stopAll  = useCallback(() => { mic.stop();  system.stop();  }, [mic, system]);

  return { mic, system, startAll, stopAll };
}

// ── Small UI helpers ──────────────────────────────────────────────────────────
function QTypeBadge({ type }) {
  if (!type) return null;
  const cls = Q_TYPE_COLOR[type] || "text-neutral-400 border-white/15 bg-white/5";
  return (
    <span className={`inline-flex items-center text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 border mb-4 ${cls}`}>
      {type}
    </span>
  );
}

function CopyBtn({ text, id, copied, onCopy }) {
  return (
    <button onClick={() => onCopy(text, id)} className="p-1 text-neutral-600 hover:text-[#D3FF24] transition shrink-0">
      {copied === id
        ? <Check className="w-3.5 h-3.5 text-[#D3FF24]" strokeWidth={2.5} />
        : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function CaptureStatusBar({ captureState, level, label }) {
  const STATE = {
    waiting:      { dot: "bg-neutral-500",  pulse: false, label: "Listening for speech…",     barColor: "#3a3a3a" },
    speaking:     { dot: "bg-green-400",    pulse: true,  label: "Capturing speech",           barColor: "#4ade80" },
    transcribing: { dot: "bg-[#D3FF24]",    pulse: true,  label: "Processing…",               barColor: "#D3FF24" },
  };
  const cfg = STATE[captureState] || STATE.waiting;

  return (
    <div className="flex items-center gap-2.5 px-4 py-1.5 border-b border-white/10 bg-black/40">
      <span className={`w-2 h-2 rounded-full shrink-0 ${cfg.dot} ${cfg.pulse ? "animate-pulse" : ""}`} />
      <span className="text-[9px] uppercase tracking-widest text-neutral-400 font-semibold flex-shrink-0">
        {cfg.label}
      </span>
      {captureState === "transcribing" && (
        <Loader2 className="w-3 h-3 text-[#D3FF24] animate-spin shrink-0" />
      )}
      {captureState !== "transcribing" && (
        <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-75"
            style={{ width: `${captureState === "speaking" ? level : Math.min(level, 15)}%`, backgroundColor: cfg.barColor }}
          />
        </div>
      )}
      <span className="text-[9px] text-neutral-600 shrink-0 uppercase tracking-widest">
        {label}
      </span>
    </div>
  );
}

// Renders both channels' status at once — the candidate's mic and the
// interviewer's system audio capture concurrently, not one-at-a-time.
function DualCaptureStatusBar({ mic, system }) {
  return (
    <>
      {system.active && <CaptureStatusBar captureState={system.captureState} level={system.level} label="Interviewer" />}
      {mic.active    && <CaptureStatusBar captureState={mic.captureState}    level={mic.level}    label="You" />}
    </>
  );
}

// Each question owns its answer and disclosure state throughout the session.
function AnswerCard({ answer, isLive, copied, copyAnswer }) {
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [showAlts, setShowAlts] = useState(false);
  return <article data-testid="question-answer" className="border-b border-white/10 pb-4 mb-5">
    <div className="border-l-2 border-white/15 pl-3 mb-4">
      <div className="text-[9px] uppercase tracking-widest text-neutral-600 mb-0.5">Question asked</div>
      <div className="text-xs text-neutral-400 leading-relaxed">{answer.question}</div>
    </div>

    {isLive && <QTypeBadge type={answer.qType} />}

    <div className="border-l-4 border-[#D3FF24] pl-4 bg-[#0D0D0D] p-4 mb-3">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-[#D3FF24] flex items-center gap-1.5 font-semibold">
          <Star className="w-3 h-3 fill-[#D3FF24]" /> AI answer
        </div>
        <CopyBtn text={answer.best} id={`${answer.id}-best`} copied={copied} onCopy={copyAnswer} />
      </div>
      <div className="text-sm leading-relaxed whitespace-pre-wrap text-white">{stripMd(answer.best)}</div>
      {answer.status && answer.status !== "complete" && (
        <p className="text-xs text-neutral-400 mt-2" role="status">
          {answer.status === "listening" ? "Listening…" : answer.status === "queued" ? "Waiting for the previous answer…" : answer.status === "error" ? "Answer unavailable. Please ask again." : "Generating…"}
        </p>
      )}
    </div>

    {isLive && answer.followUp && (
      <div className="mb-3">
        <button onClick={() => setShowFollowUp((v) => !v)}
          className="w-full flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-[#D3FF24]/30 text-xs text-neutral-500 hover:text-neutral-300 transition bg-[#0A0A0A]">
          <Zap className="w-3 h-3 text-[#D3FF24]" />
          <span>Follow-up prep</span>
          <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${showFollowUp ? "rotate-180" : ""}`} />
        </button>
        {showFollowUp && (
          <div className="border border-[#D3FF24]/20 border-t-0 bg-[#D3FF24]/[0.03] px-4 py-3">
            <div className="text-xs text-[#D3FF24]/80 leading-relaxed">{answer.followUp}</div>
          </div>
        )}
      </div>
    )}

    {isLive && answer.alts?.length > 0 && (
      <div className="mb-3">
        <button onClick={() => setShowAlts((v) => !v)}
          className="w-full flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-white/20 text-xs text-neutral-500 hover:text-neutral-300 transition bg-[#0A0A0A]">
          <ListOrdered className="w-3 h-3" />
          <span>{answer.alts.length} alternative{answer.alts.length > 1 ? "s" : ""}</span>
          <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${showAlts ? "rotate-180" : ""}`} />
        </button>
        {showAlts && answer.alts.map((alt, i) => (
          <div key={i} className="border border-white/10 border-t-0 bg-[#0D0D0D] p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{alt.label}</div>
              <CopyBtn text={alt.text} id={`${answer.id}-alt-${i}`} copied={copied} onCopy={copyAnswer} />
            </div>
            <div className="text-xs leading-relaxed whitespace-pre-wrap text-neutral-300">{stripMd(alt.text)}</div>
          </div>
        ))}
      </div>
    )}

  </article>;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function InterviewSession() {
  const { id } = useParams();
  const nav    = useNavigate();

  const [session,      setSession]     = useState(null);
  const [transcript,   setTranscript]  = useState([]);
  const [streaming,    setStreaming]   = useState(false);
  const [answers,      setAnswers]     = useState([]);
  const [liveFeed,     setLiveFeed]    = useState([]); // raw dual-speaker transcript: [{speaker, text, ts}]
  const [manualQ,      setManualQ]     = useState("");
  const [videoMode,    setVideoMode]   = useState(null);
  const [speechPanel,  setSpeechPanel] = useState(false);
  const [copied,       setCopied]      = useState(null);
  const [leftPct,      setLeftPct]     = useState(45);
  const [videoPct,     setVideoPct]    = useState(38);

  const videoRef         = useRef(null);
  const screenStreamRef  = useRef(null);
  const containerRef     = useRef(null);
  const rightPanelRef    = useRef(null);
  const dragRef          = useRef(null);
  const answerPanelRef   = useRef(null);
  const questionSequence = useRef(0);
  const manualQRef       = useRef("");
  const transcriptEndRef = useRef(null);
  const autoSendTimer    = useRef(null);
  // Ref so onTranscript can call the always-current doAskAI without stale closure
  const doAskAIRef       = useRef(null);
  // Questions asked while a previous answer is still streaming are queued
  // here instead of firing concurrently — an interviewer follow-up mid-answer
  // must never corrupt the answer currently on screen.
  const askQueueRef      = useRef([]);
  const askBusyRef       = useRef(false);

  // Transcript callback: populate input + auto-send once the interviewer has
  // stayed quiet for AUTO_SEND_SILENCE_MS.
  const updateManualQ = (text) => {
    manualQRef.current = text;
    setManualQ(text);
  };
  const onTranscript = useCallback((text) => {
    const next = mergeTranscript(manualQRef.current, text);
    manualQRef.current = next;
    setManualQ(next);
    clearTimeout(autoSendTimer.current);
    autoSendTimer.current = setTimeout(() => {
      const question = manualQRef.current.trim();
      if (question) {
        manualQRef.current = "";
        setManualQ("");
        doAskAIRef.current?.(question);
      }
    }, AUTO_SEND_SILENCE_MS);
  }, []);

  // Raw transcript feed for the right-panel Live Transcript — every segment
  // either side actually said, not just the interviewer question/AI answer
  // pairs. Consecutive segments from the same speaker within 4s are merged
  // into one line (VAD "turns" from the realtime API don't always align
  // with natural sentence breaks, so a single utterance can arrive as a few
  // short segments in a row).
  //
  // Segments are also persisted to the backend so the full call transcript
  // survives a page reload / shows up later in Session History — not just
  // the AI Q&A pairs. `pendingLocalRef` tracks the one entry still being
  // merged into (as opposed to the hydrated tail loaded from the backend,
  // which is already persisted and must not be re-sent).
  const pendingLocalRef = useRef(null);
  const persistSegment = useCallback((seg) => {
    if (!seg) return;
    api.post(`/sessions/${id}/live-feed`, { speaker: seg.speaker, text: seg.text }).catch(() => {});
  }, [id]);
  const flushPendingSegment = useCallback(() => {
    if (pendingLocalRef.current) {
      persistSegment(pendingLocalRef.current);
      pendingLocalRef.current = null;
    }
  }, [persistSegment]);
  const pushSegment = useCallback((speaker, text) => {
    setLiveFeed((feed) => {
      const last = feed[feed.length - 1];
      const isLocalLast = !!(pendingLocalRef.current && last && last.tsMs === pendingLocalRef.current.tsMs);
      if (last && last.speaker === speaker && Date.now() - last.tsMs < 4000) {
        const merged = { ...last, text: mergeTranscript(last.text, text), tsMs: Date.now() };
        pendingLocalRef.current = merged;
        return [...feed.slice(0, -1), merged];
      }
      if (isLocalLast) persistSegment(last);
      const next = { speaker, text, ts: new Date(), tsMs: Date.now() };
      pendingLocalRef.current = next;
      return [...feed, next];
    });
  }, [persistSegment]);

  // Fires when either capture channel's websocket is force-closed by the
  // backend's 90-minute cap (codes 4403/4408 — see useRealtimeChannel).
  // Guarded so a near-simultaneous close on both mic + system channels only
  // updates state / toasts once.
  const sessionExpiredRef = useRef(false);
  const handleSessionExpired = useCallback(() => {
    if (sessionExpiredRef.current) return;
    sessionExpiredRef.current = true;
    flushPendingSegment();
    setSession((s) => (s ? { ...s, status: "ended", ended_reason: "timeout" } : s));
    toast.error("Interview session ended — 90 minute limit reached.");
  }, [flushPendingSegment]);

  const capture = useAudioCapture(onTranscript, pushSegment, id, handleSessionExpired);

  // ── Load session, then auto-start capture ──────────────────────────────────
  useEffect(() => {
    api.get(`/sessions/${id}`).then((r) => {
      setSession(r.data);
      const loadedTranscript = r.data.transcript || [];
      setTranscript(loadedTranscript);
      // Repopulate the left AI-answer panel from history — the transcript
      // holds every question/answer pair, but `answers` (what the panel
      // renders) is otherwise only filled live, leaving reopened sessions
      // blank on the left.
      setAnswers(rebuildAnswers(loadedTranscript, r.data.mode === "live"));
      setLiveFeed((r.data.live_feed || []).map((seg) => ({
        ...seg, ts: new Date(seg.ts), tsMs: new Date(seg.ts).getTime(),
      })));
    }).catch(() => { toast.error("Session not found"); nav("/app"); });
    return () => { clearTimeout(autoSendTimer.current); stopVideo(); capture.stopAll(); flushPendingSegment(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-start mic once session loads (getUserMedia needs no user gesture).
  // Interviewer capture (getDisplayMedia, video+audio together) is NOT
  // auto-started — browsers reject screen/window sharing outright unless the
  // call happens inside a real click handler, so it's wired to the video
  // panel's "Share Meeting" button instead (the single entry point for
  // this — see startInterviewerShare). Nudge the candidate to click it so
  // both sides get captured. Skipped entirely for a session that's already
  // over — reopening it from history should be a read-only replay, not a
  // fresh capture prompt.
  useEffect(() => {
    if (session?.id && session.status === "active") {
      capture.mic.start();
      toast("Click \"Share Meeting\" in the video panel and pick the Zoom/Teams window — that one share captures the interviewer's video and audio together. Browsers require a click for this.", { duration: 8000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  const draftQuestion = mergeTranscript(manualQ, capture.system.partial).trim();
  const hasDraftQuestion = Boolean(draftQuestion);
  // Follow new questions only when the reader is already at the top.
  useEffect(() => {
    const panel = answerPanelRef.current;
    if (panel && panel.scrollTop < 80) panel.scrollTop = 0;
  }, [answers.length, hasDraftQuestion]);
  useEffect(() => { transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [liveFeed]);

  // ── Drag-to-resize ─────────────────────────────────────────────────────────
  const onMouseMove = useCallback((e) => {
    if (!dragRef.current) return;
    const { type, startX, startY, startPct } = dragRef.current;
    if (type === "vertical") {
      const W = containerRef.current?.offsetWidth || 1;
      setLeftPct(Math.min(70, Math.max(25, startPct + ((e.clientX - startX) / W) * 100)));
    } else {
      const H = rightPanelRef.current?.offsetHeight || 1;
      setVideoPct(Math.min(75, Math.max(15, startPct + ((e.clientY - startY) / H) * 100)));
    }
  }, []);
  const onMouseUp = useCallback(() => { dragRef.current = null; }, []);
  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup",  onMouseUp);
    return () => { window.removeEventListener("mousemove", onMouseMove); window.removeEventListener("mouseup", onMouseUp); };
  }, [onMouseMove, onMouseUp]);
  const startVDrag = (e) => { e.preventDefault(); dragRef.current = { type: "vertical",   startX: e.clientX, startPct: leftPct }; };
  const startHDrag = (e) => { e.preventDefault(); dragRef.current = { type: "horizontal", startY: e.clientY, startPct: videoPct }; };

  // ── Video ──────────────────────────────────────────────────────────────────
  // Stopping video always stops the interviewer audio channel too: the only
  // way the "screen" video mode is entered is via startInterviewerShare
  // below, which uses ONE getDisplayMedia grant for both the video preview
  // and interviewer-audio transcription — they share one lifecycle.
  // capture.system.stop() is a no-op if that channel isn't running.
  const stopVideo = () => {
    capture.system.stop();
    screenStreamRef.current?.getTracks().forEach((t) => t.stop());
    screenStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setVideoMode(null);
  };

  const startCamera = async () => {
    try {
      stopVideo();
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      screenStreamRef.current = s;
      if (videoRef.current) videoRef.current.srcObject = s;
      setVideoMode("camera");
      s.getVideoTracks()[0].onended = () => stopVideo();
    } catch (e) { if (e.name !== "NotAllowedError") toast.error("Camera: " + e.message); }
  };

  // Share the interviewer's Zoom/Teams window ONCE and get both their video
  // (shown here) and their system audio (fed into the interviewer/system
  // transcription channel) from that single grant — no need to share the
  // same window twice. The candidate's mic keeps running independently the
  // whole time (see the auto-start effect above).
  const startInterviewerShare = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      stopVideo();
      screenStreamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setVideoMode("screen");
      stream.getVideoTracks()[0].onended = () => stopVideo();

      if (stream.getAudioTracks().length) {
        await capture.system.start(stream);
      } else {
        toast.error(
          'Video shared, but no audio was included, so interviewer questions won\'t auto-trigger ' +
          'answers. Re-share and check "Share tab audio" (Chrome tab) or "Share system audio" ' +
          '(Entire screen) to capture both — or type/paste questions manually.'
        );
      }
    } catch (e) {
      if (e.name !== "NotAllowedError") toast.error("Screen share: " + e.message);
    }
  };

  // ── Clipboard ──────────────────────────────────────────────────────────────
  const copyAnswer = useCallback((text, key) => {
    navigator.clipboard.writeText(text)
      .then(() => { setCopied(key); setTimeout(() => setCopied(null), 2000); })
      .catch(() => toast.error("Copy failed"));
  }, []);

  // ── AI request ─────────────────────────────────────────────────────────────
  // Runs one question through the AI. Not called directly — see doAskAI below,
  // which queues instead of overlapping so a mid-answer interviewer follow-up
  // never corrupts the answer currently streaming to screen.
  const runAsk = async ({ id: answerId, question }) => {
    const updateAnswer = (changes) => setAnswers((prev) => prev.map((answer) =>
      answer.id === answerId ? { ...answer, ...changes } : answer
    ));
    setStreaming(true);
    updateAnswer({ status: "streaming" });
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/wingman/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: id, question }),
      });
      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Session expired.");
        updateAnswer({ status: "error" });
        setSession((s) => (s ? { ...s, status: "ended", ended_reason: s.ended_reason || "timeout" } : s));
        return;
      }
      if (!res.ok || !res.body) throw new Error("AI request failed");
      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "", full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6);
          if (raw === "[DONE]") continue;
          // Payload is JSON-encoded server-side (see backend's
          // json.dumps({'delta': ...})) specifically so an embedded newline
          // in the model's output — e.g. the blank line right after a
          // "**BEST ANSWER:**" marker — can never collide with the "\n\n"
          // frame separator used above. A raw (unescaped) chunk containing
          // "\n\n" used to get split into a piece that keeps the "data: "
          // prefix and a piece that doesn't, and the latter was silently
          // dropped — truncating whatever word/sentence started right after
          // the break.
          let delta = "";
          let payload;
          try { payload = JSON.parse(raw); } catch (_) { continue; }
          if (payload.error) throw new Error("AI request failed");
          delta = payload.delta || "";
          if (!delta) continue;
          full += delta;
          const best = session?.mode === "live" ? full
            .replace(/\*\*Q TYPE:\*\*[^*\n]*/i, "")
            .split(/\*\*(?:[⚡▸★]?\s*)?(?:FOLLOW-UP PREP|OPTION [BC23]):/i)[0]
            .replace(/\*\*(?:★\s*)?BEST ANSWER:\*\*/i, "").trim() : full;
          updateAnswer({ best });
          window.wingmanBridge?.streamChunk({ question, text: full });
        }
      }
      if (!full.trim()) throw new Error("Empty AI answer");
      const isLive = session?.mode === "live";
      const parsed = isLive ? parseAnswers(full) : { best: full, qType: null, followUp: null, alts: [] };
      updateAnswer({ ...parsed, status: "complete" });
      window.wingmanBridge?.sendAnswer({ question, answer: full });
      setTranscript((t) => [...t,
        { role: "user",      content: question },
        { role: "assistant", content: full },
      ]);
    } catch (_) { updateAnswer({ status: "error" }); toast.error("AI request failed"); }
    finally { setStreaming(false); }
  };

  // Queues a question instead of firing it immediately. If the interviewer
  // asks a follow-up while the candidate is still mid-delivery of the current
  // answer, that new question is captured (self-voice gating no longer drops
  // it — see useAudioCapture) and queued here so the next answer is ready
  // right as the current one finishes, instead of the two streams stomping on
  // each other's on-screen text.
  const doAskAI = (question) => {
    if (!question.trim()) return;
    const answer = { id: `question-${++questionSequence.current}`, question: question.trim(), best: "", status: "queued", ts: new Date() };
    setAnswers((prev) => [answer, ...prev]);
    askQueueRef.current.push(answer);
    drainAskQueue();
  };

  const drainAskQueue = async () => {
    if (askBusyRef.current) return;
    const next = askQueueRef.current.shift();
    if (!next) return;
    askBusyRef.current = true;
    try { await runAsk(next); }
    finally {
      askBusyRef.current = false;
      if (askQueueRef.current.length) drainAskQueue();
    }
  };

  // Keep ref current so onTranscript always calls the latest doAskAI
  doAskAIRef.current = doAskAI;

  const isLive = session?.mode === "live";
  const isActive = session?.status === "active";
  if (!session) return <div className="p-10 text-neutral-500 text-sm">Loading session…</div>;

  return (
    <div className="h-screen flex flex-col select-none" style={{ paddingBottom: speechPanel ? 364 : 0 }}>

      {/* ── Top bar ───────────────────────────────────────────────────────── */}
      <div className="border-b border-white/10 px-8 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={() => nav("/app")} data-testid="back-btn" className="text-neutral-500 hover:text-white">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <span className="font-display font-semibold text-lg">{session.position}</span>
              <span className="text-neutral-500">@</span>
              <span className="text-neutral-300">{session.company}</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-3 mt-1">
              <span>{session.mode}</span>·<span>{session.scenario}</span>·
              <span className="flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#D3FF24]" />{session.model}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {capture.system.active && (
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest">
              {capture.system.captureState === "speaking"     && <><span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" /><span className="text-green-400">Capturing</span></>}
              {capture.system.captureState === "transcribing" && <><Loader2 className="w-3 h-3 text-[#D3FF24] animate-spin" /><span className="text-[#D3FF24]">Processing</span></>}
              {capture.system.captureState === "waiting"      && <><span className="w-2 h-2 rounded-full bg-neutral-600" /><span className="text-neutral-500">Listening</span></>}
            </div>
          )}
          {streaming && <Loader2 className="w-4 h-4 animate-spin text-[#D3FF24]" />}
          <button
            onClick={() => setSpeechPanel((v) => !v)}
            className={`flex items-center gap-1.5 text-xs uppercase tracking-widest px-3 py-2 border transition ${
              speechPanel ? "bg-[#D3FF24]/10 border-[#D3FF24]/40 text-[#D3FF24]" : "border-white/15 text-neutral-400 hover:text-white hover:border-white/30"
            }`}
          >
            <Activity className="w-3.5 h-3.5" /> Speech
          </button>
          {isActive ? (
            <button data-testid="end-session-btn"
              onClick={async () => { stopVideo(); capture.stopAll(); flushPendingSegment(); await api.post(`/sessions/${id}/end`); toast.success("Session ended"); nav("/app/sessions"); }}
              className="text-xs uppercase tracking-widest border border-[#FF3B30]/40 text-[#FF3B30] px-4 py-2 hover:bg-[#FF3B30]/10">
              End session
            </button>
          ) : (
            <span className="text-xs uppercase tracking-widest border border-white/10 text-neutral-500 px-4 py-2">
              Session ended{session.ended_reason === "timeout" ? " · 90 min limit" : ""}
            </span>
          )}
        </div>
      </div>

      {/* ── Main panels ───────────────────────────────────────────────────── */}
      <div ref={containerRef} className="flex-1 flex overflow-hidden">

        {/* ══════════ LEFT: AI Answer Teleprompter ══════════ */}
        <div className="flex flex-col overflow-hidden" style={{ width: `${leftPct}%` }}>

          {/* Panel header */}
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between flex-shrink-0 bg-[#0A0A0A]">
            <div className="flex items-center gap-2">
              <Star className="w-3.5 h-3.5 fill-[#D3FF24] text-[#D3FF24]" />
              <span className="text-[10px] uppercase tracking-widest text-[#D3FF24] font-semibold">AI Answer</span>
              {answers.length > 0 && <span className="text-[10px] text-neutral-600 ml-1">Q{answers.length}</span>}
            </div>

            {/* Mic toggle only — the meeting/interviewer share lives solely in
                the video panel's "Share Meeting" button (right side). That's
                the single, one-click entry point for capturing the
                interviewer's window: it grabs both their video and audio
                from one getDisplayMedia grant. No duplicate control here. */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => capture.mic.active ? capture.mic.stop() : capture.mic.start()}
                disabled={!isActive}
                className={`flex items-center gap-1 px-2 py-1.5 text-[9px] uppercase tracking-widest transition border disabled:opacity-30 disabled:cursor-not-allowed ${
                  capture.mic.active ? "border-[#D3FF24]/40 text-[#D3FF24]" : "border-white/10 text-neutral-600 hover:text-white hover:border-white/20"
                }`}
                title="Microphone — your own voice"
              >
                <Mic className="w-3 h-3" /> {capture.mic.active ? "On" : "Off"}
              </button>
            </div>
          </div>

          {/* Live capture state bar — one row per channel */}
          <DualCaptureStatusBar mic={capture.mic} system={capture.system} />

          {/* New questions stay at the top; previous full Q&A pairs remain below. */}
          <div ref={answerPanelRef} className="flex-1 overflow-y-auto transcript-scroll px-5 py-4 min-h-0" data-testid="answer-history">
            {draftQuestion && (
              <AnswerCard answer={{ id: "draft", question: draftQuestion, best: "", status: "listening" }} isLive={isLive} copied={copied} copyAnswer={copyAnswer} />
            )}
            {answers.map((answer) => (
              <AnswerCard key={answer.id} answer={answer} isLive={isLive} copied={copied} copyAnswer={copyAnswer} />
            ))}
            {!draftQuestion && answers.length === 0 && (
              <div className="text-center py-12 space-y-3">
                <Mic className="w-8 h-8 text-neutral-700 mx-auto" strokeWidth={1.25} />
                <p className="text-sm text-neutral-600">{isActive ? "Listening — share meeting audio or type a question below" : "No questions recorded"}</p>
                <p className="text-xs text-neutral-700">Questions appear live with an AI answer directly below</p>
              </div>
            )}
          </div>

          {/* Question input */}
          <div className="border-t border-white/10 p-3 flex gap-2 flex-shrink-0 bg-[#0A0A0A]">
            <input
              data-testid="manual-question-input"
              value={capture.system.partial ? `${manualQ}${manualQ ? " " : ""}${capture.system.partial}` : manualQ}
              onChange={(e) => { clearTimeout(autoSendTimer.current); updateManualQ(e.target.value); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  clearTimeout(autoSendTimer.current);
                  doAskAI(manualQ);
                  updateManualQ("");
                }
              }}
              disabled={!isActive}
              placeholder={!isActive ? "Session ended — read only" : (capture.mic.active || capture.system.active) ? "Speak — transcription appears here automatically" : "Type a question and press Enter…"}
              className="flex-1 bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#D3FF24] outline-none disabled:opacity-40"
            />
            <button
              data-testid="ask-btn"
              onClick={() => { clearTimeout(autoSendTimer.current); doAskAI(manualQ); updateManualQ(""); }}
              disabled={!manualQ.trim() || !isActive}
              className="bg-[#D3FF24] text-black px-4 text-xs uppercase tracking-widest font-semibold hover:brightness-105 disabled:opacity-40"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* ── Vertical drag divider ── */}
        <div onMouseDown={startVDrag}
          className="w-1 bg-white/10 hover:bg-[#D3FF24]/50 active:bg-[#D3FF24] cursor-col-resize flex-shrink-0 transition-colors" />

        {/* ══════════ RIGHT: Video + Live Transcript ══════════ */}
        <div ref={rightPanelRef} className="flex-1 flex flex-col bg-[#0C0C0C] overflow-hidden">

          {/* Video */}
          <div className="relative bg-black flex-shrink-0 overflow-hidden" style={{ height: `${videoPct}%` }}>
            <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-contain" />
            {!videoMode && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                <VideoOff className="w-8 h-8 text-neutral-600" strokeWidth={1.25} />
                <p className="text-[10px] uppercase tracking-widest text-neutral-600">Select a video source</p>
                {isActive ? (
                  <div className="flex gap-3">
                    <button onClick={startCamera}
                      className="flex items-center gap-2 px-4 py-2 border border-white/15 text-white text-xs uppercase tracking-widest hover:bg-white/5 transition">
                      <Camera className="w-3.5 h-3.5" /> Camera
                    </button>
                    <button onClick={startInterviewerShare}
                      className="flex items-center gap-2 px-4 py-2 border border-[#D3FF24]/40 text-[#D3FF24] text-xs uppercase tracking-widest hover:bg-[#D3FF24]/5 transition"
                      title="Share the Zoom/Teams window — captures the interviewer's video and audio together">
                      <Monitor className="w-3.5 h-3.5" /> Share Meeting
                    </button>
                  </div>
                ) : (
                  <p className="text-[10px] uppercase tracking-widest text-neutral-700">No video for a past session</p>
                )}
              </div>
            )}
            {videoMode && (
              <div className="absolute top-2 right-2 flex items-center gap-2">
                <div className="flex items-center gap-1.5 bg-black/60 px-2 py-1 text-[10px] uppercase tracking-widest text-[#D3FF24]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D3FF24] animate-pulse" />
                  {videoMode === "camera" ? "Camera" : "Meeting"}
                </div>
                {videoMode === "screen"
                  ? <button onClick={startCamera} title="Switch to camera (stops interviewer audio capture)" className="bg-black/60 p-1.5 text-white hover:text-[#D3FF24]"><Camera className="w-3.5 h-3.5" /></button>
                  : <button onClick={startInterviewerShare} title="Share the meeting window instead (also captures interviewer audio)" className="bg-black/60 p-1.5 text-white hover:text-[#D3FF24]"><Monitor className="w-3.5 h-3.5" /></button>
                }
                <button onClick={stopVideo} className="bg-black/60 p-1.5 text-white hover:text-[#FF3B30]"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>

          <div onMouseDown={startHDrag}
            className="h-1 bg-white/10 hover:bg-[#D3FF24]/60 active:bg-[#D3FF24] cursor-row-resize flex-shrink-0 transition-colors" />

          {/* Live Transcript */}
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5 text-neutral-500" />
                <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Live Transcript</span>
              </div>
              <div className="flex items-center gap-3">
                {(capture.mic.active || capture.system.active) && (
                  <span className="text-[9px] uppercase tracking-widest text-[#D3FF24] flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D3FF24] animate-pulse" />
                    Realtime AI
                  </span>
                )}
                {liveFeed.length > 0 && (
                  <span className="text-[10px] text-neutral-600">{liveFeed.length} segment{liveFeed.length !== 1 ? "s" : ""}</span>
                )}
              </div>
            </div>

            {/* Raw dual-speaker transcript — everything either side actually said,
                captured concurrently from the mic (candidate) and system-audio
                (interviewer) channels. AI-generated answers stay in the left panel. */}
            <div className="flex-1 overflow-y-auto transcript-scroll px-4 py-4 min-h-0 space-y-2.5" data-testid="copilot-output">
              {liveFeed.map((seg, i) => {
                const isInterviewer = seg.speaker === "interviewer";
                return (
                  <div key={i} className={`flex items-start gap-3 slide-in ${isInterviewer ? "" : "pl-4"}`}>
                    <span className={`text-[9px] uppercase tracking-widest font-bold block text-right shrink-0 mt-0.5 ${
                      isInterviewer ? "w-16 text-neutral-600" : "w-12 text-[#D3FF24]/50"
                    }`}>
                      {isInterviewer ? "Interviewer" : "You"}
                    </span>
                    <div className={`flex-1 px-3 py-2 border ${
                      isInterviewer ? "bg-[#111] border-white/[0.07]" : "bg-[#0E0E0E] border-[#D3FF24]/15"
                    }`}>
                      <div className={`text-xs leading-relaxed ${isInterviewer ? "text-neutral-400" : "text-neutral-300"}`}>{seg.text}</div>
                      <div className="text-[9px] text-neutral-700 mt-1">{seg.ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
                    </div>
                  </div>
                );
              })}

              {streaming && (
                <div className="flex items-center gap-2 text-xs text-neutral-600 pl-4">
                  <Loader2 className="w-3 h-3 animate-spin text-[#D3FF24]" />
                  <span>Generating answer…</span>
                </div>
              )}

              {liveFeed.length === 0 && (
                <div className="text-center py-10 space-y-3">
                  <MessageSquare className="w-7 h-7 text-neutral-700 mx-auto" strokeWidth={1.25} />
                  <p className="text-xs text-neutral-600">
                    {capture.system.active
                      ? "Listening to both sides — speak and words will appear here as they're said"
                      : capture.mic.active
                        ? "Mic listening — click \"Share Meeting\" in the video panel to also capture the interviewer"
                        : "Starting capture…"}
                  </p>
                </div>
              )}
              <div ref={transcriptEndRef} />
            </div>
          </div>
        </div>
      </div>

      {speechPanel && <SpeechReflectionPanel session={session} onClose={() => setSpeechPanel(false)} />}
    </div>
  );
}
